// src/pages/DebtStudio/components/ScaleBuilder/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "lib/dayjs";
import { priceFromYield } from "lib/bondMath";
import { dayCountFraction } from "lib/dayCount";
import {
  createLinearInterpolator,
  createCubicSplineInterpolator,
} from "commons-math-interpolation";
import { supabase } from "services/supabaseClient";
import {
  useDebtStudio,
  validateDealForm,
} from "pages/DebtStudio/context/DebtStudioContext";
import ShiftControls from "./ShiftControls";
import ScaleChart from "./ScaleChart";
import ScaleGrid from "./ScaleGrid";
import OasModelLab from "./OasModelLab";
import {
  tenorToYears,
  computeShiftDeltas,
} from "pages/DebtStudio/lib/scaleUtils";
import { normalizeScale } from "pages/DebtStudio/lib/structureUtils";

const BENCHMARK_CURVES = [
  {
    id: "muni-aaa-go",
    label: "Muni AAA GO",
    prefix: "muni-aaa-go-",
    tenors: ["3m", "6m", ...Array.from({ length: 30 }, (_, i) => `${i + 1}y`)],
  },
  {
    id: "muni-a-go",
    label: "Muni A GO",
    prefix: "muni-a-go-",
    tenors: ["3m", "6m", ...Array.from({ length: 30 }, (_, i) => `${i + 1}y`)],
  },
  {
    id: "muni-aaa-txbl",
    label: "Muni AAA Taxable",
    prefix: "muni-aaa-txbl-",
    tenors: ["3m", "6m", ...Array.from({ length: 30 }, (_, i) => `${i + 1}y`)],
  },
  {
    id: "muni-a-txbl",
    label: "Muni A Taxable",
    prefix: "muni-a-txbl-",
    tenors: ["3m", "6m", ...Array.from({ length: 30 }, (_, i) => `${i + 1}y`)],
  },
  {
    id: "ustreasury",
    label: "US Treasury",
    prefix: "ust-",
    tenors: ["1m", "3m", "1y", "2y", "3y", "5y", "7y", "10y", "20y", "30y"],
  },
];

function extrapolateBeyond(x, xs, ys) {
  const n = xs.length;
  const slope = (ys[n - 1] - ys[n - 2]) / (xs[n - 1] - xs[n - 2]);
  return ys[n - 1] + slope * (x - xs[n - 1]);
}

function roundTo(value, decimals) {
  if (value == null || isNaN(value)) return null;
  const f = Math.pow(10, decimals);
  return Math.round(value * f) / f;
}

export default function ScaleBuilder() {
  const {
    form,
    structureRows,
    setStructureRows,
    scale,
    setScale,
    benchmarkCurveId,
    setBenchmarkCurveId,
    interpMethod,
    setInterpMethod,
  } = useDebtStudio();

  const skeleton = structureRows || [];
  const datedDate = form?.datedDate || dayjs().format("YYYY-MM-DD");
  const deliveryDate = form?.deliveryDate || datedDate;
  const defaultCouponUsed = form?.couponRate ?? 5;
  const dayCount = form?.dayCount || "30/360";
  const couponFrequency = form?.couponFrequency || "semiannual";

  const selectedCurve = useMemo(
    () =>
      BENCHMARK_CURVES.find((c) => c.id === benchmarkCurveId) ||
      BENCHMARK_CURVES[0],
    [benchmarkCurveId]
  );

  const points = useMemo(
    () =>
      (selectedCurve?.tenors || []).map((tenor) => ({
        security_id: selectedCurve.prefix + tenor,
        tenor,
      })),
    [selectedCurve]
  );

  const securityIds = useMemo(
    () => points.map((p) => p.security_id),
    [points]
  );

  const [loading, setLoading] = useState(false);
  const [baseMap, setBaseMap] = useState({});
  const [adjustMap, setAdjustMap] = useState({});
  const [adjustHistory, setAdjustHistory] = useState([]);
  const [couponOverrides, setCouponOverrides] = useState({});
  const [oasApplyHistory, setOasApplyHistory] = useState([]);
  const [error, setError] = useState(null);
  const [shiftPreset, setShiftPreset] = useState("none");
  const [shiftMagnitude, setShiftMagnitude] = useState(25);
  const [asOfDate, setAsOfDate] = useState(null);

  useEffect(() => {
    if (!securityIds.length || !skeleton.length) return;
    let mounted = true;
    setError(null);
    setLoading(true);

    async function fetchFn(asOfDate, ids) {
      const { data, error } = await supabase
        .from("market_data")
        .select("security_id,value,date")
        .in("security_id", ids)
        .lte("date", asOfDate)
        .order("date", { ascending: false });

      if (error) throw error;

      const seen = new Set();
      const result = [];
      for (const r of data || []) {
        if (seen.has(r.security_id)) continue;
        seen.add(r.security_id);
        result.push({
          security_id: r.security_id,
          rate: r.value == null ? null : Number(r.value),
          date: r.date,
        });
      }
      return result;
    }

    fetchFn(dayjs().format("YYYY-MM-DD"), securityIds)
      .then((rows) => {
        if (!mounted) return;
        const map = {};
        rows?.forEach((r) => {
          map[r.security_id] = r.rate;
        });
        setBaseMap(map);
        const latest = rows?.reduce(
          (max, r) => (r.date > max ? r.date : max),
          rows[0]?.date || null
        );
        setAsOfDate(latest);
        setShiftPreset("none");
      })
      .catch((err) => mounted && setError(String(err?.message ?? err)))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [selectedCurve.id, skeleton, securityIds]);

  useEffect(() => {
    if (!skeleton.length || !baseMap || loading) return;
    const incoming = scale || null;

    if (incoming) {
      const map = Object.fromEntries(incoming.map((r) => [r.date, r]));
      setAdjustMap(
        skeleton.reduce((acc, row) => {
          acc[row.date] = map[row.date]?.adjustment_bps ?? 0;
          return acc;
        }, {})
      );
      setCouponOverrides(
        skeleton.reduce((acc, row) => {
          acc[row.date] = map[row.date]?.coupon ?? defaultCouponUsed;
          return acc;
        }, {})
      );
    } else {
      setAdjustMap(
        skeleton.reduce((a, r) => {
          a[r.date] = 0;
          return a;
        }, {})
      );
      setCouponOverrides(
        skeleton.reduce((a, r) => {
          a[r.date] = defaultCouponUsed;
          return a;
        }, {})
      );
    }

    setShiftPreset("none");
  }, [skeleton, baseMap, loading, scale, defaultCouponUsed]);

  function getInterpolator(method, xs, ys) {
    const valid = xs
      .map((x, i) => ({ x, y: ys[i] }))
      .filter((p) => p.y != null && !Number.isNaN(p.y))
      .sort((a, b) => a.x - b.x);

    const vx = valid.map((p) => p.x);
    const vy = valid.map((p) => p.y);
    if (vx.length < 2) return null;

    return method === "linear"
      ? createLinearInterpolator(vx, vy)
      : createCubicSplineInterpolator(vx, vy);
  }

  const rows = useMemo(() => {
    const xs = points.map((p) => tenorToYears(p.tenor));
    const ys = points.map((p) =>
      baseMap[p.security_id] == null ? null : Number(baseMap[p.security_id])
    );
    const interp = getInterpolator(interpMethod, xs, ys);

    return (skeleton || []).map((row) => {
      const years = dayCountFraction(datedDate, row.date, dayCount);

      let bench = null;
      if (years != null) {
        if (years < 0.25) bench = ys[0];
        else if (years > 30) bench = extrapolateBeyond(years, xs, ys);
        else if (interp) bench = interp(years);
      }

      const benchmark = bench == null ? null : roundTo(bench, 2);
      const bps = Number(adjustMap[row.date] ?? 0);
      const yRaw = benchmark == null ? null : benchmark + bps / 100;
      const y = yRaw == null ? null : roundTo(yRaw, 2);
      const cRaw = couponOverrides[row.date] ?? defaultCouponUsed;
      const c = roundTo(cRaw, 3);

      let price = null;
      if (years != null && c != null && y != null && deliveryDate) {
        try {
          price = priceFromYield({
            couponRate: c / 100,
            yieldRate: y / 100,
            deliveryDate,
            maturityDate: row.call_date && c > y ? row.call_date : row.date,
            dayCount,
            frequency: couponFrequency,
          });
        } catch {
          price = null;
        }
      }

      return {
        ...row,
        date: row.date,
        benchmark,
        adjustment_bps: bps,
        yield: y,
        coupon: c,
        price,
      };
    });
  }, [
    skeleton,
    points,
    baseMap,
    interpMethod,
    adjustMap,
    couponOverrides,
    defaultCouponUsed,
    datedDate,
    dayCount,
    couponFrequency,
    deliveryDate,
  ]);

  const { ok } = validateDealForm(form);
  if (!ok)
    return (
      <div className="text-red-500 text-sm p-4">
        Please complete Issue Info and Dates &amp; Terms
      </div>
    );
  if (!structureRows?.length)
    return (
      <div className="text-red-500 text-sm p-4">
        No structure has been created yet. Please finish entering required Deal
        Info and Dates &amp; Terms.
      </div>
    );

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-app text-sm">
          Market as of {asOfDate ? dayjs(asOfDate).format("M/D/YYYY") : "—"}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            const norm = normalizeScale(rows);
            setScale(norm);
            setStructureRows((prev) => {
              if (!Array.isArray(prev) || prev.length === 0) return prev;

              const same =
                prev.length === norm.length &&
                prev.every(
                  (r, i) =>
                    Number(r.coupon) === Number(norm[i]?.coupon) &&
                    Number(r.yield) === Number(norm[i]?.yield) &&
                    Number(r.price) === Number(norm[i]?.price)
                );
              if (same) return prev;

              const merged = prev.map((r) => {
                const match = norm.find((x) => x.date === r.date);
                if (!match) return r;
                return {
                  ...r,
                  coupon: match.coupon ?? r.coupon,
                  yield: match.yield ?? r.yield,
                  price: match.price ?? r.price,
                };
              });

              return merged;
            });
          }}
        >
          Apply Scale
        </button>
      </div>

      {loading && <div className="text-app mb-2">Loading benchmark curve...</div>}
      {error && <div className="text-red-500 mb-2">{error}</div>}

      <div className="flex flex-wrap items-center gap-6 mb-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-app mr-1">Benchmark curve</label>
          <div className="relative inline-block">
            <select
              value={benchmarkCurveId}
              onChange={(e) => setBenchmarkCurveId(e.target.value)}
              className="appearance-none pr-9 pl-3 h-8 text-sm rounded-md border border-input bg-background text-foreground shadow-sm"
              style={{ minWidth: 140 }}
            >
              {BENCHMARK_CURVES.map((curve) => (
                <option key={curve.id} value={curve.id}>
                  {curve.label}
                </option>
              ))}
            </select>
            <svg
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.2l3.71-2.97a.75.75 0 1 1 .94 1.17l-4.18 3.35a.75.75 0 0 1-.94 0L5.25 8.4a.75.75 0 0 1-.02-1.19z" />
            </svg>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-app mr-1">Interpolation</label>
          <div className="relative inline-block">
            <select
              value={interpMethod}
              onChange={(e) => setInterpMethod(e.target.value)}
              className="appearance-none pr-9 pl-3 h-8 text-sm rounded-md border border-input bg-background text-foreground shadow-sm"
              style={{ minWidth: 120 }}
            >
              <option value="linear">Linear</option>
              <option value="spline">Cubic spline</option>
            </select>
            <svg
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.2l3.71-2.97a.75.75 0 1 1 .94 1.17l-4.18 3.35a.75.75 0 0 1-.94 0L5.25 8.4a.75.75 0 0 1-.02-1.19z" />
            </svg>
          </div>
        </div>

        <div className="text-muted text-xs ml-2">
          Short maturities (&lt;1y) use 3m, 6m, 1y. Beyond 30y extrapolated from
          29–30y slope.
        </div>
      </div>

      <ScaleChart rows={rows} />

      <div className="mt-3">
        <ShiftControls
          shiftPreset={shiftPreset}
          setShiftPreset={setShiftPreset}
          shiftMagnitude={shiftMagnitude}
          setShiftMagnitude={setShiftMagnitude}
          applyShift={() => {
            if (shiftPreset === "none") return;
            setAdjustHistory((p) => [...p, { ...adjustMap }]);
            const deltas = computeShiftDeltas(
              shiftPreset,
              shiftMagnitude,
              skeleton
            );
            setAdjustMap((prev) => {
              const next = { ...prev };
              for (const d in deltas) {
                next[d] = Math.round((prev[d] ?? 0) + (deltas[d] ?? 0));
              }
              return next;
            });
          }}
          undoShift={() => {
            setAdjustHistory((p) => {
              if (!p.length) return p;
              const last = p[p.length - 1];
              setAdjustMap(last);
              return p.slice(0, -1);
            });
            setShiftPreset("none");
          }}
          clearAdjustments={() => {
            setAdjustHistory([]);
            setAdjustMap(
              skeleton.reduce((a, r) => {
                a[r.date] = 0;
                return a;
              }, {})
            );
            setShiftPreset("none");
          }}
          loading={loading}
          prevExists={adjustHistory.length > 0}
        />
      </div>

      <div className="mt-4">
        <ScaleGrid
          rows={rows}
          onBpsChange={(date, val) => {
            const n = val === "" ? "" : Number(val);
            setAdjustMap((m) => ({
              ...m,
              [date]:
                n === "" || Number.isNaN(n) ? 0 : Math.round(n),
            }));
          }}
          onCouponOverrideChange={(date, val) => {
            setCouponOverrides((p) => ({
              ...p,
              [date]:
                val === "" || val == null
                  ? defaultCouponUsed
                  : roundTo(Number(val), 3),
            }));
          }}
        />
      </div>

      {selectedCurve.id === "muni-aaa-go" ? <OasModelLab
        rows={rows}
        settlementDate={deliveryDate}
        marketAsOfDate={asOfDate}
        dayCount={dayCount}
        couponFrequency={couponFrequency}
        onApplyScenario={({ date, coupon, spreadBps }) => {
          const currentRow = rows.find((row) => row.date === date);
          setOasApplyHistory((history) => [
            ...history,
            {
              date,
              coupon: Number(currentRow?.coupon ?? defaultCouponUsed),
              spreadBps: Number(currentRow?.adjustment_bps ?? 0),
            },
          ]);
          setCouponOverrides((previous) => ({
            ...previous,
            [date]: roundTo(coupon, 3),
          }));
          setAdjustMap((previous) => ({
            ...previous,
            [date]: Math.round(spreadBps),
          }));
        }}
        lastAppliedScenario={oasApplyHistory[oasApplyHistory.length - 1] || null}
        onUndoScenario={() => {
          const prior = oasApplyHistory[oasApplyHistory.length - 1];
          if (!prior) return;
          setCouponOverrides((previous) => ({
            ...previous,
            [prior.date]: roundTo(prior.coupon, 3),
          }));
          setAdjustMap((previous) => ({
            ...previous,
            [prior.date]: Math.round(prior.spreadBps),
          }));
          setOasApplyHistory((history) => history.slice(0, -1));
        }}
      /> : null}
    </>
  );
}
